/**
 * Utility functions for email generation
 * Handles patient application link generation for email notifications
 */

/**
 * Get the base URL for the application
 * Uses environment variable if available, otherwise falls back to window.location.origin
 * For production, can be overridden with REACT_APP_BASE_URL
 */
const getBaseUrl = () => {
  // Check for environment variable first
  if (typeof process !== 'undefined' && process.env?.REACT_APP_BASE_URL) {
    return process.env.REACT_APP_BASE_URL;
  }
  
  // Fallback to window.location.origin for browser context
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  
  // Final fallback (for production)
  return 'https://samriddhihealthcare.com'; // Update with actual production URL
};

/**
 * Determine recipient role based on email address
 * @param {string} email - Recipient email address
 * @returns {string} Role identifier
 */
const getRecipientRole = (email) => {
  if (!email) return 'admin';
  
  const emailLower = email.toLowerCase();
  
  // Hospital emails
  if (emailLower.includes('hospital') || (!emailLower.includes('@samhealth.co.in') && !emailLower.includes('@samriddhi.health'))) {
    return 'hospital';
  }
  
  // Doctor emails
  if (emailLower.includes('doctor') || emailLower.includes('medical')) {
    return 'doctor';
  }
  
  // Finance emails
  if (emailLower.includes('finance') || emailLower.includes('cssg')) {
    return 'finance';
  }
  
  // Policy emails
  if (emailLower.includes('policy')) {
    return 'policy';
  }
  
  // Admin/Support emails (default)
  return 'admin';
};

/**
 * Generate patient application URL based on recipient role
 * @param {string} applicationId - Application ID
 * @param {string} recipientEmail - Recipient email address (optional, used to determine role)
 * @param {string} recipientRole - Explicit recipient role (optional, overrides email-based detection)
 * @returns {string} Full URL to the patient application page
 */
export const getPatientApplicationUrl = (applicationId, recipientEmail = null, recipientRole = null) => {
  const baseUrl = getBaseUrl();
  
  // Determine role
  let role = recipientRole;
  if (!role && recipientEmail) {
    role = getRecipientRole(recipientEmail);
  }
  
  // Default to admin if no role determined
  if (!role) {
    role = 'admin';
  }
  
  // Map role to route
  const routeMap = {
    'hospital': `/hospital/application/${applicationId}`,
    'doctor': `/doctor/application-review/${applicationId}`,
    'finance': `/dashboard/financial/review/${applicationId}`,
    'policy': `/policy/details/${applicationId}`,
    'admin': `/admin/hospital-details/${applicationId}`
  };
  
  const route = routeMap[role] || routeMap['admin'];
  return `${baseUrl}${route}`;
};

/**
 * Generate HTML link button for patient application
 * @param {string} applicationId - Application ID
 * @param {string} patientName - Patient name (optional)
 * @param {string} recipientEmail - Recipient email address (optional)
 * @param {string} recipientRole - Explicit recipient role (optional)
 * @param {string} linkText - Custom link text (optional)
 * @returns {string} HTML string for styled link button
 */
export const getPatientApplicationLinkHtml = (
  applicationId,
  patientName = null,
  recipientEmail = null,
  recipientRole = null,
  linkText = null
) => {
  const url = getPatientApplicationUrl(applicationId, recipientEmail, recipientRole);
  
  // Generate link text
  let text = linkText;
  if (!text) {
    if (patientName) {
      text = `View Patient Application for ${patientName} (${applicationId})`;
    } else {
      text = `View Application ${applicationId}`;
    }
  }
  
  // Return styled button HTML
  return `
    <div style="margin: 20px 0;">
      <a href="${url}" 
         style="display: inline-block; padding: 12px 24px; background-color: #0f766e; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 14px;">
        ${text}
      </a>
    </div>
  `;
};

/**
 * Generate simple HTML link for patient application
 * @param {string} applicationId - Application ID
 * @param {string} patientName - Patient name (optional)
 * @param {string} recipientEmail - Recipient email address (optional)
 * @param {string} recipientRole - Explicit recipient role (optional)
 * @param {string} linkText - Custom link text (optional)
 * @returns {string} HTML string for link
 */
export const getPatientApplicationLinkSimple = (
  applicationId,
  patientName = null,
  recipientEmail = null,
  recipientRole = null,
  linkText = null
) => {
  const url = getPatientApplicationUrl(applicationId, recipientEmail, recipientRole);
  
  // Generate link text
  let text = linkText;
  if (!text) {
    if (patientName) {
      text = `View Patient Application for ${patientName}`;
    } else {
      text = `View Application ${applicationId}`;
    }
  }
  
  return `<a href="${url}" style="color: #0f766e; text-decoration: underline;">${text}</a>`;
};

